import { useAuthRuntime } from "@frontend/components/auth/auth-runtime"
import { Button } from "@frontend/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@frontend/components/ui/dialog"
import { Progress } from "@frontend/components/ui/progress"
import { cn } from "@frontend/lib/utils"
import { useQueryClient } from "@tanstack/react-query"
import { AlertTriangle, FolderOpen, Loader2, Upload } from "lucide-react"
import { type ChangeEvent, type DragEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"

import { buildUploadPlan } from "../lib/case-upload-parser"
import type {
  DiscoveredUploadFile,
  ParsedUploadPlan,
  UploadBatchManifest,
  UploadWorkflow
} from "../types/case-upload-types"

interface CaseUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  workflow: UploadWorkflow
}

type DataTransferItemWithEntry = DataTransferItem & {
  webkitGetAsEntry?: () => FileSystemEntry | null
}

interface FileSystemEntryLike {
  isFile: boolean
  isDirectory: boolean
  name: string
}

interface FileSystemFileEntryLike extends FileSystemEntryLike {
  file: (successCallback: (file: File) => void, errorCallback?: (error: DOMException) => void) => void
}

interface FileSystemDirectoryEntryLike extends FileSystemEntryLike {
  createReader: () => FileSystemDirectoryReaderLike
}

interface FileSystemDirectoryReaderLike {
  readEntries: (
    successCallback: (entries: FileSystemEntryLike[]) => void,
    errorCallback?: (error: DOMException) => void
  ) => void
}

function normalizeRelativePath(rawPath: string): string {
  return rawPath.replace(/\\/g, "/").replace(/^\/+/, "")
}

function dedupeDiscoveredFiles(items: DiscoveredUploadFile[]): DiscoveredUploadFile[] {
  const byKey = new Map<string, DiscoveredUploadFile>()
  for (const item of items) {
    const normalizedPath = normalizeRelativePath(item.relativePath || item.file.name)
    const key = `${normalizedPath}::${item.file.size}::${item.file.lastModified}`
    if (!byKey.has(key)) {
      byKey.set(key, { file: item.file, relativePath: normalizedPath })
    }
  }
  return Array.from(byKey.values())
}

function readFileEntry(entry: FileSystemFileEntryLike): Promise<File> {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject)
  })
}

function readDirectoryEntries(reader: FileSystemDirectoryReaderLike): Promise<FileSystemEntryLike[]> {
  return new Promise((resolve, reject) => {
    const entries: FileSystemEntryLike[] = []

    const readBatch = () => {
      reader.readEntries(
        (batch) => {
          if (!batch || batch.length === 0) {
            resolve(entries)
            return
          }
          entries.push(...batch)
          readBatch()
        },
        (error) => reject(error)
      )
    }

    readBatch()
  })
}

async function walkEntry(entry: FileSystemEntryLike, parentPath: string, out: DiscoveredUploadFile[]): Promise<void> {
  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntryLike
    const file = await readFileEntry(fileEntry)
    const relativePath = parentPath ? `${parentPath}/${file.name}` : file.name
    out.push({ file, relativePath })
    return
  }

  if (entry.isDirectory) {
    const directoryEntry = entry as FileSystemDirectoryEntryLike
    const nextPath = parentPath ? `${parentPath}/${entry.name}` : entry.name
    const children = await readDirectoryEntries(directoryEntry.createReader())
    for (const child of children) {
      await walkEntry(child, nextPath, out)
    }
  }
}

function discoveredFromFileList(fileList: FileList): DiscoveredUploadFile[] {
  const items: DiscoveredUploadFile[] = []
  for (const file of Array.from(fileList)) {
    const fileWithPath = file as File & { webkitRelativePath?: string }
    const relativePath =
      fileWithPath.webkitRelativePath && fileWithPath.webkitRelativePath.length > 0
        ? fileWithPath.webkitRelativePath
        : file.name
    items.push({ file, relativePath: normalizeRelativePath(relativePath) })
  }
  return dedupeDiscoveredFiles(items)
}

async function discoveredFromDropEvent(event: DragEvent<HTMLDivElement>): Promise<DiscoveredUploadFile[]> {
  const output: DiscoveredUploadFile[] = []
  const itemEntries: FileSystemEntryLike[] = []
  for (const item of Array.from(event.dataTransfer.items)) {
    const maybeEntryItem = item as DataTransferItemWithEntry
    if (typeof maybeEntryItem.webkitGetAsEntry !== "function") {
      continue
    }
    const entry = maybeEntryItem.webkitGetAsEntry()
    if (!entry) continue
    itemEntries.push(entry as unknown as FileSystemEntryLike)
  }

  if (itemEntries.length > 0) {
    for (const entry of itemEntries) {
      await walkEntry(entry, "", output)
    }
    if (output.length > 0) {
      return dedupeDiscoveredFiles(output)
    }
  }

  return discoveredFromFileList(event.dataTransfer.files)
}

function uploadManifestWithProgress(params: {
  projectId: string
  manifest: UploadBatchManifest
  files: File[]
  token: string | null
  onProgress: (percent: number) => void
}): Promise<void> {
  const { projectId, manifest, files, token, onProgress } = params

  const parseErrorMessage = (raw: string, status: number, fallback: string): string => {
    if (!raw) return `${fallback} (${status})`
    try {
      const parsed = JSON.parse(raw) as { error?: string; message?: string }
      return parsed.error || parsed.message || `${fallback} (${status})`
    } catch {
      return `${fallback} (${status})`
    }
  }

  const bytesToHex = (bytes: Uint8Array): string =>
    Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("")

  const sleep = (ms: number): Promise<void> =>
    new Promise((resolve) => {
      setTimeout(resolve, ms)
    })

  const authHeaders: Record<string, string> = {}
  if (token) {
    authHeaders.Authorization = `Bearer ${token}`
  }

  return (async () => {
    if (files.length === 0) {
      throw new Error("No files to upload")
    }

    const startResponse = await fetch(`/api/projects/${projectId}/cases/upload/chunked/start`, {
      method: "POST",
      headers: {
        ...authHeaders,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        manifest,
        files: files.map((file) => ({
          name: file.name,
          size: file.size
        }))
      })
    })

    const startRaw = await startResponse.text()
    if (!startResponse.ok) {
      throw new Error(parseErrorMessage(startRaw, startResponse.status, "Upload initialization failed"))
    }

    let startPayload: { session_id?: string; chunk_size_bytes?: number } | null = null
    try {
      startPayload = startRaw ? (JSON.parse(startRaw) as { session_id?: string; chunk_size_bytes?: number }) : null
    } catch {
      throw new Error("Upload initialization returned invalid JSON")
    }

    const sessionId = startPayload?.session_id
    const chunkSize = startPayload?.chunk_size_bytes
    if (!(sessionId && chunkSize) || chunkSize < 1) {
      throw new Error("Upload initialization response is missing required fields")
    }

    const totalBytes = files.reduce((sum, file) => sum + file.size, 0)
    let completedBytes = 0
    const maxRetriesPerChunk = 3

    for (let fileIndex = 0; fileIndex < files.length; fileIndex += 1) {
      const file = files[fileIndex]!
      let offset = 0

      while (offset < file.size) {
        const chunkEnd = Math.min(offset + chunkSize, file.size)
        const chunkBuffer = await file.slice(offset, chunkEnd).arrayBuffer()
        const chunkSha = bytesToHex(new Uint8Array(await crypto.subtle.digest("SHA-256", chunkBuffer)))

        let nextOffset = offset
        let attempt = 0
        while (attempt < maxRetriesPerChunk) {
          attempt += 1
          const chunkResponse = await fetch(
            `/api/projects/${projectId}/cases/upload/chunked/${sessionId}/files/${fileIndex}/chunk`,
            {
              method: "PUT",
              headers: {
                ...authHeaders,
                "Content-Type": "application/octet-stream",
                "x-upload-offset": String(offset),
                "x-upload-total-size": String(file.size),
                "x-upload-chunk-sha256": chunkSha
              },
              body: chunkBuffer
            }
          )

          const chunkRaw = await chunkResponse.text()
          const chunkPayload = (() => {
            if (!chunkRaw) return null
            try {
              return JSON.parse(chunkRaw) as { uploaded_bytes?: number; expected_offset?: number }
            } catch {
              return null
            }
          })()

          if (chunkResponse.ok) {
            const acknowledged = chunkPayload?.uploaded_bytes ?? chunkEnd
            if (acknowledged <= offset || acknowledged > file.size) {
              throw new Error("Chunk acknowledgment was invalid")
            }
            nextOffset = acknowledged
            break
          }

          if (
            chunkResponse.status === 409 &&
            typeof chunkPayload?.expected_offset === "number" &&
            chunkPayload.expected_offset >= 0 &&
            chunkPayload.expected_offset <= file.size
          ) {
            nextOffset = chunkPayload.expected_offset
            break
          }

          const message = parseErrorMessage(chunkRaw, chunkResponse.status, "Chunk upload failed")
          if (attempt >= maxRetriesPerChunk) {
            throw new Error(message)
          }
          await sleep(attempt * 400)
        }

        if (nextOffset === offset) {
          throw new Error("Chunk upload made no progress")
        }

        offset = nextOffset
        const progressValue = Math.min(99, Math.round(((completedBytes + offset) / totalBytes) * 100))
        onProgress(progressValue)
      }

      completedBytes += file.size
    }

    const completeResponse = await fetch(`/api/projects/${projectId}/cases/upload/chunked/${sessionId}/complete`, {
      method: "POST",
      headers: authHeaders
    })
    const completeRaw = await completeResponse.text()
    if (!completeResponse.ok) {
      throw new Error(parseErrorMessage(completeRaw, completeResponse.status, "Upload finalization failed"))
    }

    onProgress(100)
  })()
}

export function CaseUploadDialog({ open, onOpenChange, projectId, workflow }: CaseUploadDialogProps) {
  const queryClient = useQueryClient()
  const { getAccessToken } = useAuthRuntime()

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const folderInputRef = useRef<HTMLInputElement | null>(null)

  const [dragActive, setDragActive] = useState(false)
  const [isParsing, setIsParsing] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [plan, setPlan] = useState<ParsedUploadPlan | null>(null)
  const [parseErrors, setParseErrors] = useState<string[]>([])
  const [serverError, setServerError] = useState<string | null>(null)
  const [discoveredCount, setDiscoveredCount] = useState(0)

  useEffect(() => {
    if (open) return
    setDragActive(false)
    setIsParsing(false)
    setIsUploading(false)
    setUploadProgress(0)
    setPlan(null)
    setParseErrors([])
    setServerError(null)
    setDiscoveredCount(0)
  }, [open])

  const workflowText = useMemo(() => {
    if (workflow === "radiology") {
      return "Drop folders or files containing matched .nii/.nii.gz and .json sidecar pairs."
    }

    return "Drop folders or files containing OME-TIFF files and/or OME-Zarr directories."
  }, [workflow])

  const runParse = useCallback(
    async (discoveredFiles: DiscoveredUploadFile[]) => {
      if (discoveredFiles.length === 0) {
        setPlan(null)
        setParseErrors(["No files were selected."])
        return
      }

      setIsParsing(true)
      setServerError(null)
      setUploadProgress(0)
      setDiscoveredCount(discoveredFiles.length)
      try {
        const nextPlan = await buildUploadPlan(discoveredFiles, workflow)
        setPlan(nextPlan)
        setParseErrors(nextPlan.errors)
      } finally {
        setIsParsing(false)
      }
    },
    [workflow]
  )

  const onDrop = useCallback(
    async (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      setDragActive(false)
      const discovered = await discoveredFromDropEvent(event)
      await runParse(discovered)
    },
    [runParse]
  )

  const onFileInputChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files
      if (!fileList) return
      await runParse(discoveredFromFileList(fileList))
      event.target.value = ""
    },
    [runParse]
  )

  const onUpload = useCallback(async () => {
    if (!plan?.manifest) {
      return
    }

    setIsUploading(true)
    setServerError(null)
    setUploadProgress(0)
    try {
      const token = await getAccessToken()
      await uploadManifestWithProgress({
        projectId,
        manifest: plan.manifest,
        files: plan.files,
        token,
        onProgress: setUploadProgress
      })
      await queryClient.invalidateQueries({ queryKey: ["projects", projectId, "cases"] })
      onOpenChange(false)
    } catch (error) {
      setServerError(error instanceof Error ? error.message : "Upload failed")
    } finally {
      setIsUploading(false)
    }
  }, [getAccessToken, onOpenChange, plan, projectId, queryClient])

  const canUpload = Boolean(plan?.manifest) && !isParsing && !isUploading

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(95vw,980px)] sm:max-w-[980px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Cases</DialogTitle>
          <DialogDescription>{workflowText}</DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            "rounded-lg border border-dashed p-6 transition-colors",
            dragActive ? "border-primary bg-muted/60" : "border-border bg-muted/20"
          )}
          onDragEnter={(event) => {
            event.preventDefault()
            setDragActive(true)
          }}
          onDragOver={(event) => {
            event.preventDefault()
            setDragActive(true)
          }}
          onDragLeave={(event) => {
            event.preventDefault()
            setDragActive(false)
          }}
          onDrop={(event) => {
            void onDrop(event)
          }}
        >
          <div className="flex items-start gap-3">
            <Upload className="size-5 mt-0.5 text-muted-foreground" />
            <div className="space-y-3 flex-1">
              <p className="text-sm font-medium">Drag and drop folders or files here</p>
              <p className="text-xs text-muted-foreground">{workflowText}</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isParsing || isUploading}
                >
                  <Upload className="size-4 mr-1" />
                  Select Files
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => folderInputRef.current?.click()}
                  disabled={isParsing || isUploading}
                >
                  <FolderOpen className="size-4 mr-1" />
                  Select Folder
                </Button>
              </div>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            multiple
            onChange={(event) => {
              void onFileInputChange(event)
            }}
          />
          <input
            ref={folderInputRef}
            type="file"
            className="hidden"
            multiple
            onChange={(event) => {
              void onFileInputChange(event)
            }}
            {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {isParsing
                ? "Parsing selected files..."
                : discoveredCount > 0
                  ? `Discovered ${discoveredCount} input files.`
                  : "No files parsed yet."}
            </span>
            {plan?.manifest ? <span>{plan.previewCases.length} cases ready</span> : null}
          </div>

          {parseErrors.length > 0 ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 space-y-2">
              <div className="flex items-center gap-2 text-destructive text-sm font-medium">
                <AlertTriangle className="size-4" />
                Validation issues
              </div>
              <ul className="text-xs text-destructive space-y-1">
                {parseErrors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {serverError ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
              {serverError}
            </div>
          ) : null}

          {plan?.previewCases.length ? (
            <div className="rounded-md border overflow-hidden">
              <div className="max-h-[320px] overflow-auto divide-y">
                {plan.previewCases.map((uploadCase) => (
                  <div key={uploadCase.study_identifier} className="p-3 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium truncate">{uploadCase.study_identifier}</p>
                      <span className="text-xs text-muted-foreground">{uploadCase.series.length} series</span>
                    </div>
                    {uploadCase.study_description ? (
                      <p className="text-xs text-muted-foreground truncate">{uploadCase.study_description}</p>
                    ) : null}
                    <div className="space-y-1">
                      {uploadCase.series.map((series) => (
                        <div
                          key={`${uploadCase.study_identifier}-${series.series_identifier}`}
                          className="text-xs text-muted-foreground flex items-center justify-between gap-2"
                        >
                          <span className="truncate">{series.series_identifier}</span>
                          <span className="font-mono uppercase">{series.format}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {isUploading || uploadProgress > 0 ? (
            <div className="space-y-1">
              <Progress value={uploadProgress} />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{isUploading ? "Uploading..." : "Upload complete"}</span>
                <span className="tabular-nums">{uploadProgress}%</span>
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isUploading}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void onUpload()} disabled={!canUpload}>
            {isUploading ? (
              <>
                <Loader2 className="size-4 mr-1 animate-spin" />
                Uploading
              </>
            ) : (
              "Upload Cases"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
